import React, { useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, Center, Grid, Group, Loader, Paper, Select, SimpleGrid, Stack, Table, Text, Title } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { collection, collectionGroup, onSnapshot, query, where } from "firebase/firestore";
import { PieChart } from "@mui/x-charts/PieChart";
import { useFirestore } from "../../helpers/firebaseContext";
import useFirestoreCollection from "../../helpers/useFirestoreCollection";
import { CustomClaims, Fund, FundState, Order, OrderStatusCode } from "../../types";
import { MyUserDataContext } from "../../contexts";
import { Margin, usePDF } from 'react-to-pdf';

const LAST_SELECTED_FUND_KEY = "closedEndedFundProgress:lastFund";

type SegmentRow = {
    segment: string;
    clients: number;
    amount: number;
    paidAmount?: number;
    target?: number;
    gap?: number;
    pct?: number;
};

const eligibleStates: FundState[] = ["internal_only", "commitment_only", "ready_for_investors", "closed_to_new_investors"];

const scaleValue = (value: number) => {
    const abs = Math.abs(value);
    if (abs >= 1_000_000_000) return { scaled: value / 1_000_000_000, unit: "B" };
    if (abs >= 1_000_000) return { scaled: value / 1_000_000, unit: "M" };
    if (abs >= 1_000) return { scaled: value / 1_000, unit: "K" };
    return { scaled: value, unit: "" };
};

const formatAmount = (value: number, currency?: string) => {
    if (!Number.isFinite(value)) return "--";
    const { scaled, unit } = scaleValue(value);
    const formatter = new Intl.NumberFormat("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    return `${formatter.format(scaled)}${unit}${currency ? ` ${currency}` : ""}`;
};

const getSegment = (order: Order) =>
    order.referredBy?.segment ||
    order.rm?.segment ||
    order.submittedBy?.segment ||
    "Unspecified";

const ClosedEndedFundProgress = () => {
    const db = useFirestore();
    const fundsQuery = query(
        collection(db, "funds"),
        where("type", "==", "closed_ended")
    );
    const appContext = useContext(MyUserDataContext);
    let userClaims = appContext.firebaseTokenResult?.claims.customClaims as CustomClaims;

    const isAdmin = !!userClaims?.admin || !!userClaims?.cr;
    const isRM = !!userClaims?.kfh_oper;
    const isRestrictedRM = isRM && !isAdmin;


    const { data: fundsData, loading: fundsLoading } = useFirestoreCollection(fundsQuery, true);
    const funds = useMemo(
        () => ((fundsData as Fund[]) || []).filter((f) => eligibleStates.includes(f.fund_state as FundState)),
        [fundsData]
    );

    const [selectedFundId, setSelectedFundId] = useState<string | null>(null);
    const [orders, setOrders] = useState<Order[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersError, setOrdersError] = useState<string | null>(null);
    const { toPDF, targetRef } = usePDF({
        filename: 'fund_progress.pdf',
        page: {
            margin: Margin.MEDIUM,

        },
    });

    useEffect(() => {
        if (!funds.length) return;
        setSelectedFundId((current) => {
            // If current selection is still valid, keep it.
            if (current && funds.some((f) => f.id === current)) return current;

            // Try saved selection.
            const savedFundId = localStorage.getItem(LAST_SELECTED_FUND_KEY);
            if (savedFundId && funds.some((f) => f.id === savedFundId)) return savedFundId;

            // Fallback to first fund.
            return funds[0].id;
        });
    }, [funds]);

    useEffect(() => {
        if (selectedFundId) {
            localStorage.setItem(LAST_SELECTED_FUND_KEY, selectedFundId);
        }
    }, [selectedFundId]);

    useEffect(() => {
        if (!selectedFundId) {
            setOrders([]);
            return;
        }

        setOrdersLoading(true);
        setOrdersError(null);
        const ordersQuery = query(
            collectionGroup(db, "orders"),
            where("fund.id", "==", selectedFundId)

        );

        const unsubscribe = onSnapshot(
            ordersQuery,
            (snapshot) => {
                const mappedOrders = snapshot.docs.map((doc) => {
                    const data = doc.data() as Order;
                    return { ...data, id: data.id || doc.id };
                });
                setOrders(mappedOrders);
                setOrdersLoading(false);
            },
            (error) => {
                console.error("Error fetching orders", error);
                setOrders([]);
                setOrdersLoading(false);
                setOrdersError("Could not load fund orders right now.");
            }
        );

        return () => unsubscribe();
    }, [db, selectedFundId]);

    const selectedFund = useMemo(() => funds.find((f) => f.id === selectedFundId), [funds, selectedFundId]);

    const targetsBySegment = useMemo(() => {
        const targets = selectedFund?.targets || {};
        if (isRestrictedRM && userClaims?.segment) {
            return { [userClaims.segment]: targets[userClaims.segment] || 0 };
        }
        return targets;
    }, [selectedFund, isRestrictedRM, userClaims?.segment]);

    const targetTotal = useMemo(() => Object.values(targetsBySegment || {}).reduce((acc, value) => acc + (Number(value) || 0), 0), [targetsBySegment]);

    const aggregate = useMemo(() => {
        let filteredOrders = orders;
        if (isRestrictedRM && userClaims?.segment) {
            filteredOrders = orders.filter((o) => getSegment(o) === userClaims.segment);
        }

        if (!filteredOrders || !filteredOrders.length) {
            return {
                commitmentRows: [] as SegmentRow[],
                actualRows: [] as SegmentRow[],
                combinedRows: [] as SegmentRow[],
                orderStatusCounts: {} as Record<string, number>,
                totals: {
                    clients: 0,
                    commitmentClients: 0,
                    actualClients: 0,
                    paidClients: 0,
                    target: targetTotal,
                    commitmentAmount: 0,
                    actualAmount: 0,
                    paidAmount: 0,
                    totalExpected: 0,
                    gap: targetTotal,
                    percentOfTarget: 0,
                    percentOfTargetPaid: 0,
                    manualAmount: 0,
                    digitalAmount: 0,
                    manualClients: 0,
                    digitalClients: 0,
                }
            };
        }

        const normalizeNumber = (value: any) => {
            if (typeof value === "string") {
                value = value.replace(/,/g, "");
            }
            const numeric = typeof value === "number" ? value : Number(value);
            return Number.isFinite(numeric) ? numeric : 0;
        };

        const getCustomerId = (order: Order) =>
            order.customerObj?.id ||
            order.customerObj?.civilID ||
            order.customerObj?.mobile ||
            order.id ||
            "unknown";

        const commitmentSegments = new Map<string, { amount: number; clients: Set<string> }>();
        const actualSegments = new Map<string, { amount: number; clients: Set<string> }>();
        const paidSegments = new Map<string, { amount: number; clients: Set<string> }>();

        let commitmentAmount = 0;
        let actualAmount = 0;
        let paidAmount = 0;
        let manualAmount = 0;
        let digitalAmount = 0;
        const orderStatusCounts: Record<string, number> = {};

        const allClients = new Set<string>();
        const commitmentClients = new Set<string>();
        const actualClients = new Set<string>();
        const paidClients = new Set<string>();
        const manualClients = new Set<string>();
        const digitalClients = new Set<string>();

        const addToMap = (map: Map<string, { amount: number; clients: Set<string> }>, key: string, amount: number, clientId: string) => {
            if (!map.has(key)) {
                map.set(key, { amount: 0, clients: new Set() });
            }
            const entry = map.get(key)!;
            entry.amount += amount;
            entry.clients.add(clientId);
        };

        filteredOrders.forEach((order) => {
            const status = order.tags?.orderStatus?.code as OrderStatusCode | undefined;
            if (status === OrderStatusCode.rejected) {
                return;
            }
            if (order.type && order.type !== "subscription") {
                return;
            }

            const amount = normalizeNumber(order.postNavAmount ?? order.transferAmount ?? 0);
            if (!amount) {
                return;
            }

            const clientId = getCustomerId(order);
            const segment = getSegment(order);
            const isPaid = order.tags?.paid?.code === "paid";
            const isCommitment = !isPaid && [
                OrderStatusCode.need_signature,
                OrderStatusCode.commitment,
                OrderStatusCode.need_form,
                OrderStatusCode.waiting_certified_rm
            ].includes((status ?? OrderStatusCode.unknown) as OrderStatusCode) && !order.signature;

            const isManual = !!order.manual;
            const statusTitle = order.tags?.orderStatus?.title || order.tags?.orderStatus?.code || "Unknown";

            allClients.add(clientId);
            orderStatusCounts[statusTitle] = (orderStatusCounts[statusTitle] || 0) + 1;

            if (isCommitment) {
                commitmentAmount += amount;
                commitmentClients.add(clientId);
                addToMap(commitmentSegments, segment, amount, clientId);
            } else {
                actualAmount += amount;
                actualClients.add(clientId);
                addToMap(actualSegments, segment, amount, clientId);
                if (isPaid) {
                    paidAmount += amount;
                    paidClients.add(clientId);
                    addToMap(paidSegments, segment, amount, clientId);
                }
            }

            if (isManual) {
                manualAmount += amount;
                manualClients.add(clientId);
            } else {
                digitalAmount += amount;
                digitalClients.add(clientId);
            }
        });

        const mergeSegments = () => {
            const combined = new Map<string, { amount: number; paidAmount: number; clients: Set<string> }>();
            const merge = (map: Map<string, { amount: number; clients: Set<string> }>, isPaidMap: boolean = false) => {
                map.forEach((value, key) => {
                    if (!combined.has(key)) {
                        combined.set(key, { amount: 0, paidAmount: 0, clients: new Set() });
                    }
                    const entry = combined.get(key)!;
                    if (isPaidMap) {
                        entry.paidAmount += value.amount;
                    } else {
                        entry.amount += value.amount;
                    }
                    value.clients.forEach((c) => entry.clients.add(c));
                });
            };
            merge(commitmentSegments);
            merge(actualSegments);
            merge(paidSegments, true);
            Object.keys(targetsBySegment || {}).forEach((segment) => {
                if (!combined.has(segment)) {
                    combined.set(segment, { amount: 0, paidAmount: 0, clients: new Set() });
                }
            });
            return combined;
        };

        const buildRows = (map: Map<string, { amount: number; clients: Set<string> }>): SegmentRow[] => {
            return Array.from(map.entries())
                .map(([segment, data]) => ({
                    segment,
                    clients: data.clients.size,
                    amount: data.amount,
                }))
                .sort((a, b) => b.amount - a.amount);
        };

        const commitmentRows = buildRows(commitmentSegments);
        const actualRows = buildRows(actualSegments);

        const combinedSegments = mergeSegments();
        const combinedRows: SegmentRow[] = Array.from(combinedSegments.entries())
            .map(([segment, data]) => {
                const target = Number(targetsBySegment?.[segment] || 0);
                const gap = target - data.amount;
                const pct = target ? (data.amount / target) * 100 : undefined;
                return {
                    segment,
                    clients: data.clients.size,
                    target,
                    amount: data.amount,
                    paidAmount: data.paidAmount,
                    gap,
                    pct,
                };
            })
            .sort((a, b) => b.amount - a.amount);

        const totalExpected = actualAmount + commitmentAmount;
        const gap = targetTotal ? targetTotal - totalExpected : 0;
        const percentOfTarget = targetTotal ? (totalExpected / targetTotal) * 100 : 0;
        const percentOfTargetPaid = targetTotal ? (paidAmount / targetTotal) * 100 : 0;

        return {
            commitmentRows,
            actualRows,
            combinedRows,
            orderStatusCounts,
            totals: {
                clients: allClients.size,
                commitmentClients: commitmentClients.size,
                actualClients: actualClients.size,
                paidClients: paidClients.size,
                target: targetTotal,
                commitmentAmount,
                actualAmount,
                paidAmount,
                totalExpected,
                gap,
                percentOfTarget,
                percentOfTargetPaid,
                manualAmount,
                digitalAmount,
                manualClients: manualClients.size,
                digitalClients: digitalClients.size,
            }
        };
    }, [orders, targetTotal, targetsBySegment, isRestrictedRM, userClaims?.segment]);

    const renderTable = (title: string, rows: SegmentRow[], showTarget: boolean, totals?: { clients: number; amount: number; target?: number; gap?: number; paidAmount?: number }) => (
        <Paper shadow="sm" radius="md" p="md">
            <Title order={4} mb="xs">{title}</Title>
            <Table striped highlightOnHover withRowBorders={false} horizontalSpacing="sm" verticalSpacing="sm">
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Segment</Table.Th>
                        <Table.Th>Clients (#)</Table.Th>
                        {showTarget && <Table.Th>Target</Table.Th>}
                        <Table.Th>Amount</Table.Th>
                        {showTarget && <Table.Th>Paid</Table.Th>}
                        {showTarget && <Table.Th>% of Target</Table.Th>}
                        {showTarget && <Table.Th>Gap</Table.Th>}
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {rows.length ? rows.map((row) => (
                        <Table.Tr key={row.segment}>
                            <Table.Td><Text fw={500}>{row.segment}</Text></Table.Td>
                            <Table.Td>{row.clients}</Table.Td>
                            {showTarget && <Table.Td>{row.target ? formatAmount(row.target, selectedFund?.currency) : "--"}</Table.Td>}
                            <Table.Td>{formatAmount(row.amount, selectedFund?.currency)}</Table.Td>
                            {showTarget && <Table.Td>{formatAmount(row.paidAmount || 0, selectedFund?.currency)}</Table.Td>}
                            {showTarget && <Table.Td>{row.pct !== undefined && row.target ? `${row.pct.toFixed(2)}%` : "--"}</Table.Td>}
                            {showTarget && <Table.Td>{row.target ? formatAmount(row.gap || 0, selectedFund?.currency) : "--"}</Table.Td>}
                        </Table.Tr>
                    )) : (
                        <Table.Tr>
                            <Table.Td colSpan={showTarget ? 7 : 3}>
                                <Text c="dimmed">No data yet</Text>
                            </Table.Td>
                        </Table.Tr>
                    )}
                    {totals && (
                        <Table.Tr>
                            <Table.Td><Text fw={700}>Total</Text></Table.Td>
                            <Table.Td><Text fw={700}>{totals.clients}</Text></Table.Td>
                            {showTarget && <Table.Td><Text fw={700}>{totals.target ? formatAmount(totals.target, selectedFund?.currency) : "--"}</Text></Table.Td>}
                            <Table.Td><Text fw={700}>{formatAmount(totals.amount, selectedFund?.currency)}</Text></Table.Td>
                            {showTarget && <Table.Td><Text fw={700}>{formatAmount(totals.paidAmount || 0, selectedFund?.currency)}</Text></Table.Td>}
                            {showTarget && <Table.Td><Text fw={700}>{totals.target ? `${((totals.amount / totals.target) * 100).toFixed(2)}%` : "--"}</Text></Table.Td>}
                            {showTarget && <Table.Td><Text fw={700}>{totals.target ? formatAmount(totals.gap || 0, selectedFund?.currency) : "--"}</Text></Table.Td>}
                        </Table.Tr>
                    )}
                </Table.Tbody>
            </Table>
        </Paper>
    );

    const renderPie = (
        title: string,
        data: { id: number; value: number; label: string }[],
        valueFormatter?: (value: number) => string
    ) => {
        const total = data.reduce((acc, item) => acc + item.value, 0);
        const formattedTotal = valueFormatter ? valueFormatter(total) : formatAmount(total, selectedFund?.currency);
        return (
            <Paper shadow="sm" radius="md" p="md">
                <Group justify="space-between" mb="xs">
                    <Title order={5}>{title}</Title>
                    {total > 0 && <Text size="sm" c="dimmed">{formattedTotal}</Text>}
                </Group>
                {total > 0 ? (
                    <PieChart
                        series={[
                            {
                                data: data,
                                arcLabel: (item) => {
                                    const percentage = total ? (item.value / total) * 100 : 0;
                                    return percentage >= 8 ? `${percentage.toFixed(0)}%` : "";
                                },
                                arcLabelMinAngle: 20,
                            }
                        ]}
                        height={260}
                        colors={["#C5A548", "#550000", "#545E75", "#7FB7BE", "#262A10", "#D84A05", "#30C5FF", "#CED3DC", "#12b886"]}
                        margin={{ top: 10, bottom: 10, left: 0, right: 200 }}
                        slotProps={{
                            legend: {
                                hidden: false,
                                direction: 'column',
                                position: { vertical: 'middle', horizontal: 'right' },
                                padding: 0,
                                itemMarkWidth: 10,
                                itemMarkHeight: 10,
                                markGap: 5,
                                itemGap: 8,
                            }
                        }}
                    />
                ) : (
                    <Center h={220}><Text c="dimmed">No data yet</Text></Center>
                )}
            </Paper>
        );
    };

    const orderStatusPieData = useMemo(() => {
        return Object.entries(aggregate.orderStatusCounts || {}).map(([label, value], index) => ({
            id: index,
            value,
            label,
        }));
    }, [aggregate.orderStatusCounts]);

    if (fundsLoading) {
        return <Center py="xl"><Loader /></Center>;
    }

    if (!funds.length) {
        return (
            <Alert color="yellow" icon={<IconAlertCircle size={16} />} title="No closed-ended funds">
                There are no closed-ended funds in commitment or investor-ready states.
            </Alert>
        );
    }

    return (
        <Stack gap="md" style={{ width: "100%" }}>
            <Group justify="space-between" align="flex-start">
                <div>
                    <Title order={3}>{selectedFund?.name_en || "Fundraising performance"}</Title>
                    <Text c="dimmed" size="sm">
                        Tracking closed-ended funds in commitment, ready for investors, or closed to new investors.
                    </Text>
                </div>
                <Group>

                    <Select
                        w={300}
                        label="Fund"
                        placeholder="Pick a fund"
                        value={selectedFundId}
                        onChange={setSelectedFundId}
                        data={funds.map((fund) => ({
                            value: fund.id,
                            label: fund.name_en || fund.name || fund.id,
                        }))}
                    />
                    <Button
                        mt={"sm"}
                        onClick={() => toPDF()}>Download PDF</Button>

                </Group>
            </Group>

            {ordersError && (
                <Alert color="red" icon={<IconAlertCircle size={16} />}>
                    {ordersError}
                </Alert>
            )}
            <Stack ref={targetRef} gap="md" style={{ width: "100%" }}>


                <SimpleGrid cols={{ base: 1, md: 3, lg: 6 }}>
                    <Paper p="md" shadow="sm" radius="md">
                        <Text c="dimmed" fw={600} size="xs">Clients (#)</Text>
                        <Title order={2}>{aggregate.totals.clients}</Title>
                    </Paper>
                    <Paper p="md" shadow="sm" radius="md">
                        <Text c="dimmed" fw={600} size="xs">Target</Text>
                        <Title order={2}>{formatAmount(aggregate.totals.target || 0, selectedFund?.currency)}</Title>
                    </Paper>
                    <Paper p="md" shadow="sm" radius="md">
                        <Text c="dimmed" fw={600} size="xs">Total Expected</Text>
                        <Title order={2}>{formatAmount(aggregate.totals.totalExpected, selectedFund?.currency)}</Title>
                    </Paper>
                    <Paper p="md" shadow="sm" radius="md">
                        <Text c="dimmed" fw={600} size="xs">Gap</Text>
                        <Title order={2}>{formatAmount(aggregate.totals.gap, selectedFund?.currency)}</Title>
                    </Paper>
                    <Paper p="md" shadow="sm" radius="md">
                        <Text c="dimmed" fw={600} size="xs">% of Target</Text>
                        <Title order={2}>{aggregate.totals.percentOfTarget.toFixed(2)}%</Title>
                    </Paper>
                    <Paper p="md" shadow="sm" radius="md">
                        <Text c="dimmed" fw={600} size="xs">% of Target (paid)</Text>
                        <Title order={2}>{aggregate.totals.percentOfTargetPaid.toFixed(2)}%</Title>
                    </Paper>
                </SimpleGrid>

                {ordersLoading && (
                    <Center><Loader /></Center>
                )}

                <Grid gap="md">
                    <Grid.Col span={{ base: 12, lg: 7 }}>
                        <Stack gap="md">
                            {renderTable("Commitments", aggregate.commitmentRows, false, {
                                clients: aggregate.totals.commitmentClients,
                                amount: aggregate.totals.commitmentAmount,
                            })}
                            {renderTable("Actuals", aggregate.actualRows, false, {
                                clients: aggregate.totals.actualClients,
                                amount: aggregate.totals.actualAmount,
                            })}
                            {renderTable("Commitments plus Actuals", aggregate.combinedRows, true, {
                                clients: aggregate.totals.clients,
                                amount: aggregate.totals.totalExpected,
                                target: aggregate.totals.target,
                                gap: aggregate.totals.gap,
                                paidAmount: aggregate.totals.paidAmount,
                            })}
                        </Stack>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, lg: 5 }}>
                        <Stack gap="md">
                            {renderPie("Actual vs Commitment Amount", [
                                { id: 0, value: aggregate.totals.paidAmount, label: "Paid" },
                                { id: 1, value: aggregate.totals.actualAmount - aggregate.totals.paidAmount, label: "Actual (Unpaid)" },
                                { id: 2, value: aggregate.totals.commitmentAmount, label: "Commitment" },
                            ])}
                            {renderPie("Orders by Status", orderStatusPieData, (value) => `${new Intl.NumberFormat("en-US").format(value)} orders`)}
                            {renderPie("Amount by Subscription Method", [
                                { id: 0, value: aggregate.totals.digitalAmount, label: "Digital" },
                                { id: 1, value: aggregate.totals.manualAmount, label: "Manual" },
                            ])}
                            {renderPie("Clients by Stage", [
                                { id: 0, value: aggregate.totals.paidClients, label: "Paid" },
                                { id: 1, value: aggregate.totals.actualClients - aggregate.totals.paidClients, label: "Actual (Unpaid)" },
                                { id: 2, value: aggregate.totals.commitmentClients, label: "Commitment" },
                            ], (value) => `${new Intl.NumberFormat("en-US").format(value)} clients`)}
                            {renderPie("Clients by Subscription Method", [
                                { id: 0, value: aggregate.totals.digitalClients, label: "Digital" },
                                { id: 1, value: aggregate.totals.manualClients, label: "Manual" },
                            ], (value) => `${new Intl.NumberFormat("en-US").format(value)} clients`)}
                        </Stack>
                    </Grid.Col>
                </Grid>
            </Stack>
        </Stack>
    );
};

export default ClosedEndedFundProgress;
